import React, { createContext, useContext } from 'react';

import { useTrainSearch } from '../SearchRequestProvider';
import { createFeature, useFlow } from '@edreamsodigeo/frontend-core';
import { SearchResponseProviderProps } from './types';
import {
  UNBUNDLE_RESULTS,
  UNBUNDLE_RESULTS_DESKTOP,
  SKIPS_TO_ENABLE_RAIL_SCRAPPING,
  UNBUNDLE_RESULTS_RAILS_DESKTOP,
  UNBUNDLE_RESULTS_RAILS_MOBILE,
  UNBUNDLE_RESULTS_RAILS_MOBILE_PRICE_PER_SEGMENT,
} from 'clientState/ab/aliases';
import SearchItinerariesResponseProviderWithUnbundle from './SearchItinerariesResponseProviderWithUnbundle';
import TrainSearchItinerariesResponseProviderWithUnbundle from './TrainSearchItinerariesResponseProviderWithUnbundle';
import TrainSearchItinerariesResponseProviderWithUnbundlePricePerSegment from './TrainSearchItinerariesResponseProviderWithUnbundlePricePerSegment';

// This is only for the rest of the components to know if unbundle needs to be displayed or not
// Will be true when the alias is activated AND it's a round trip
export const UnbundleContext = createContext<{
  isUnbundle: boolean;
  isUnbundlePricePerSegment: boolean;
}>({ isUnbundle: false, isUnbundlePricePerSegment: false });

export const useUnbundle = () => useContext(UnbundleContext);

const SearchItinerariesResponseProviderMobile =
  createFeature<SearchResponseProviderProps>({
    name: UNBUNDLE_RESULTS,
    variations: [
      (props) => <SearchItinerariesResponseProviderWithUnbundle {...props} />,
      (props) => (
        <SearchItinerariesResponseProviderWithUnbundle
          {...props}
          isUnbundleEnabled
        />
      ),
      (props) => (
        <SearchItinerariesResponseProviderWithUnbundle
          {...props}
          isUnbundleEnabled
        />
      ),
    ],
    LoadingComponent: () => null,
    version: 1,
  });

const SearchItinerariesResponseProviderDesktop =
  createFeature<SearchResponseProviderProps>({
    name: UNBUNDLE_RESULTS_DESKTOP,
    variations: [
      (props) => <SearchItinerariesResponseProviderWithUnbundle {...props} />,
      (props) => (
        <SearchItinerariesResponseProviderWithUnbundle
          {...props}
          isUnbundleEnabled
        />
      ),
    ],
    LoadingComponent: () => null,
    version: 1,
  });

const SearchItineraryResponseProviderVersioned = (props) => {
  const flow = useFlow();
  return flow === 'desktop' ? (
    <SearchItinerariesResponseProviderDesktop {...props} />
  ) : (
    <SearchItinerariesResponseProviderMobile {...props} />
  );
};

const SearchItinerariesTrainResponseProviderMobile =
  createFeature<SearchResponseProviderProps>({
    name: UNBUNDLE_RESULTS_RAILS_MOBILE,
    variations: [
      (props) => (
        <TrainSearchItinerariesResponseProviderWithUnbundle {...props} />
      ),
      (props) => (
        <TrainSearchItinerariesResponseProviderWithUnbundle
          {...props}
          isUnbundleEnabled
        />
      ),
    ],
    LoadingComponent: () => null,
    version: 1,
  });

const SearchItinerariesTrainResponseProviderDesktop =
  createFeature<SearchResponseProviderProps>({
    name: UNBUNDLE_RESULTS_RAILS_DESKTOP,
    variations: [
      (props) => (
        <TrainSearchItinerariesResponseProviderWithUnbundle {...props} />
      ),
      (props) => (
        <TrainSearchItinerariesResponseProviderWithUnbundle
          {...props}
          isUnbundleEnabled
        />
      ),
    ],
    LoadingComponent: () => null,
    version: 1,
  });

const SearchItineraryTrainResponseProviderVersioned = (props) => {
  const flow = useFlow();
  return flow === 'desktop' ? (
    <SearchItinerariesTrainResponseProviderDesktop {...props} />
  ) : (
    <SearchItinerariesTrainResponseProviderMobile {...props} />
  );
};

const SearchRailScrappingVersioned = createFeature({
  name: SKIPS_TO_ENABLE_RAIL_SCRAPPING,
  variations: [
    SearchItineraryResponseProviderVersioned,
    SearchItineraryTrainResponseProviderVersioned,
  ],
  LoadingComponent: () => null,
  version: 1,
});

const SearchItinerariesTrainResponseProviderMobilePricePerSegment =
  createFeature<SearchResponseProviderProps>({
    name: UNBUNDLE_RESULTS_RAILS_MOBILE_PRICE_PER_SEGMENT,
    variations: [
      (props) => <SearchRailScrappingVersioned {...props} />,
      (props) => (
        <TrainSearchItinerariesResponseProviderWithUnbundlePricePerSegment
          {...props}
        />
      ),
    ],
    LoadingComponent: () => null,
    version: 1,
  });

const SearchItinerariesTrainResponseProviderPricePerSegment = (props) => {
  const flow = useFlow();
  return flow === 'desktop' ? (
    <SearchRailScrappingVersioned {...props} />
  ) : (
    <SearchItinerariesTrainResponseProviderMobilePricePerSegment {...props} />
  );
};

const SearchItinerariesResponseProvider: React.FC<
  SearchResponseProviderProps
> = (props) => {
  const trainSearch = useTrainSearch();
  return trainSearch ? (
    <SearchItinerariesTrainResponseProviderPricePerSegment {...props} />
  ) : (
    <SearchItineraryResponseProviderVersioned {...props} />
  );
};

export default SearchItinerariesResponseProvider;
